
import Box from '@mui/material/Box';
import EDTypography from '../Typography/EDTypography';
import { IRapportDataSetControl } from './RapportMallTypes';
import {
  AVENY_REPORT_DEFAULT_BAND_HEIGHT,
  AVENY_REPORT_DEFAULT_LINE_MARGIN,
  MM_TO_PIXEL_SCALE,
  SELECTED_SHADOW,
  getPositionStyle,
} from './RapportMallUtils';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { RapportKontrollEditPosition } from './RapportKontrollEditPosition';
import { RapportKontrollHeader } from './RapportKontrollHeader';
import { RapporteditorTextField } from '../Rapporteditor/RapporteditorTextField';
import { RapporteditorAssociatedField } from '../Rapporteditor/RapporteditorAssociatedField';
import EDIconButton from '../Buttons/IconButton/EDIconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import Divider from '@mui/material/Divider';
import RapportTypography from './RapportTypography';

const scale = MM_TO_PIXEL_SCALE;
const DATASET_CAPTION_HEIGHT = 3.5; // mm

export interface IProps {
  ctrl: IRapportDataSetControl;
  sx?: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSelect?: (path: number[]) => void;
  onUpdate?: (updatedControl: IRapportDataSetControl) => void;
  controlPath: number[];
  selected?: boolean;
  maxWidth: number;
}

const borders = (
  visible: boolean = true,
  left: boolean = true,
  top: boolean = true,
  right: boolean = true,
  bottom: boolean = true
) => ({
  borderLeft: visible && left ? '1px solid #ccc' : undefined,
  borderTop: visible && top ? '1px solid #ccc' : undefined,
  borderRight: visible && right ? '1px solid #ccc' : undefined,
  borderBottom: visible && bottom ? '1px solid #ccc' : undefined,
});

export const RapportDataSet: React.FC<IProps> = props => {
  const { ctrl, sx, onMouseEnter, onMouseLeave, selected, maxWidth } = props;
  const handleSelect = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (props.onSelect) {
      props.onSelect(props.controlPath);
    }
  };
  const rowHeight = ctrl.RowHeight ?? AVENY_REPORT_DEFAULT_BAND_HEIGHT;
  const rowCount = Math.min(
    Math.max(ctrl.MinRowCount ?? 0, ctrl.EmptyRowCount ?? 0, 1),
    ctrl.MaxRowCount ?? Infinity
  );
  const emptyRowStart = Math.max(rowCount - (ctrl.EmptyRowCount ?? 0), 0);
  const hasHeader = ctrl.Header !== false;
  const hasBorder = ctrl.Border !== false;
  const hasExtBorder = ctrl.ExtBorder === true;
  const gridHeight =
    (hasHeader ? rowHeight : 0) +
    rowCount * rowHeight +
    (hasExtBorder ? AVENY_REPORT_DEFAULT_LINE_MARGIN * 2 : 0);
  const totalHeight =
    gridHeight + (ctrl.Caption && hasExtBorder ? DATASET_CAPTION_HEIGHT : 0);
  const allocatedFieldWidths = ctrl.Fields
    ? ctrl.Fields.reduce((sum, field) => sum + (field.Width || 0), 0)
    : 0;
  const dynamicFieldCount = ctrl.Fields
    ? ctrl.Fields.filter(field => field.Width === undefined).length
    : 0;
  const availableWidth = ctrl.Width ?? maxWidth;
  const dynamicFieldWidth =
    dynamicFieldCount >= 1
      ? (availableWidth - allocatedFieldWidths) / dynamicFieldCount
      : 0;
  return (
    <Box
      sx={{
        boxShadow: selected ? SELECTED_SHADOW : undefined,
        border: ctrl.ExtBorder ? '1px solid #aaa' : undefined,
        boxSizing: 'border-box',
        padding: 0,
        ...getPositionStyle(
          ctrl,
          props.controlPath.length > 1,
          maxWidth,
          totalHeight
        ),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        ...sx,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleSelect}
      data-control-path={props.controlPath.join('-')}
    >
      {ctrl.Caption && hasExtBorder && (
        <Box
          sx={{
            height: DATASET_CAPTION_HEIGHT * scale,
          }}
        >
          <EDTypography
            variant="caption"
            fontSize={10}
            lineHeight={1}
            sx={{
              p: '2px 4px 0 4px',
              m: 0,
            }}
          >
            {ctrl.Caption}
          </EDTypography>
        </Box>
      )}
      <Box
        sx={{
          height: gridHeight * scale,
          paddingTop: 0,
          paddingLeft: hasExtBorder
            ? `${AVENY_REPORT_DEFAULT_LINE_MARGIN * 2 * scale}px`
            : 0,
          paddingRight: hasExtBorder
            ? `${AVENY_REPORT_DEFAULT_LINE_MARGIN * 2 * scale}px`
            : 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {hasHeader && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                height: rowHeight * scale,
              }}
            >
              {ctrl.Fields?.map((field, fieldIndex) => (
                <Box
                  key={fieldIndex}
                  sx={{
                    boxSizing: 'border-box',
                    padding: '1px 4px',
                    width: (field.Width ?? dynamicFieldWidth) * scale,
                    height: rowHeight * scale,
                    ...borders(hasBorder, fieldIndex === 0, true, true, true),
                    textAlign: 'left',
                    verticalAlign: 'middle',
                    overflow: 'hidden',
                  }}
                >
                  <RapportTypography Style={hasBorder ? undefined : 'b'}>
                    {field.Caption}
                  </RapportTypography>
                </Box>
              ))}
            </Box>
          )}
          {Array.from({ length: rowCount }, (_, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                height: rowHeight * scale,
              }}
            >
              {ctrl.Fields?.map((field, fieldIndex) => (
                <Box
                  key={fieldIndex}
                  sx={{
                    boxSizing: 'border-box',
                    padding: '1px 4px',
                    width: (field.Width ?? dynamicFieldWidth) * scale,
                    height: rowHeight * scale,
                    ...borders(
                      hasBorder,
                      fieldIndex === 0,
                      !hasHeader && rowIndex === 0,
                      true,
                      true
                    ),
                    textAlign: 'left',
                    verticalAlign: 'middle',
                    overflow: 'hidden',
                  }}
                >
                  {rowIndex < emptyRowStart && (
                    <RapportTypography>{field.Name}</RapportTypography>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export interface IRapportDataSetEditProps {
  value: IRapportDataSetControl;
  onChange: (ctrl: IRapportDataSetControl) => void;
}

export const RapportDataSetEdit: React.FC<IRapportDataSetEditProps> = props => (
  <Box sx={{ mt: 2 }}>
    <Box sx={{ mb: 3 }}>
      <RapportKontrollHeader value={props.value} />
      <RapporteditorTextField
        label="Undertabell"
        field="Association"
        value={props.value}
        onChange={props.onChange}
        allowModelSelect
        selectAssociation
      />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Box sx={{ flex: '0 0 66%' }}>
          <RapporteditorTextField
            label="Rubrik"
            field="Caption"
            value={props.value}
            onChange={props.onChange}
          />
        </Box>
        <Box>
          <RapporteditorTextField
            label="Radhöjd"
            field="RowHeight"
            value={props.value}
            onChange={props.onChange}
            numeric
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!props.value.ExtBorder}
              onChange={e =>
                props.onChange({ ...props.value, ExtBorder: e.target.checked })
              }
            />
          }
          label="Visa ram och rubrik"
          sx={{ mt: -2, mb: 1 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={props.value.Header !== false}
              onChange={e =>
                props.onChange({
                  ...props.value,
                  Header: e.target.checked ? undefined : false,
                })
              }
            />
          }
          label="Visa titelrad"
          sx={{ mt: -2, mb: 1 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={props.value.Border !== false}
              onChange={e =>
                props.onChange({
                  ...props.value,
                  Border: e.target.checked ? undefined : false,
                })
              }
            />
          }
          label="Visa tabellinjer"
          sx={{ mt: -2, mb: 1 }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <RapporteditorTextField
          label="Max antal rader"
          hint="Antal rader som högst ska visas i tabellen. Kan överstyra Minsta antal rader och Antal tomma rader."
          field="MaxRowCount"
          value={props.value}
          onChange={props.onChange}
          numeric
        />
        <RapporteditorTextField
          label="Minsta antal rader"
          hint="Antal rader som minst ska visas i tabellen. Fylls på med tomma rader om undertabellen har färre rader."
          field="MinRowCount"
          value={props.value}
          onChange={props.onChange}
          numeric
        />
        <RapporteditorTextField
          label="Antal tomma rader"
          hint="Antal tomma rader efter raderna från undertabellen."
          field="EmptyRowCount"
          value={props.value}
          onChange={props.onChange}
          numeric
        />
      </Box>
    </Box>
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <EDTypography variant="subtitle2">Fält</EDTypography>
        <EDIconButton
          icon={<AddCircleOutlineIcon />}
          onClick={() => {
            const newFields = [...(props.value.Fields || [])];
            newFields.push({
              Name: '',
              Caption: '',
              Width: 20,
            });
            props.onChange({ ...props.value, Fields: newFields });
          }}
          margin="none"
          padding="none"
          tooltip="Lägg till nytt fält"
        />
      </Box>
      {props.value.Fields && props.value.Fields.length > 0 ? (
        props.value.Fields.map((field, index) => (
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            <Box sx={{ flex: 1 }}>
              {index > 0 && <Divider sx={{ marginBottom: 4 }} />}
              <RapporteditorAssociatedField
                field={field}
                association={props.value.Association}
                onFieldChange={updatedField => {
                  const newFields = [...(props.value.Fields || [])];
                  newFields[index] = updatedField;
                  props.onChange({ ...props.value, Fields: newFields });
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mt: index > 0 ? 5 : 1,
              }}
            >
              <EDIconButton
                icon={<ArrowCircleUpIcon />}
                onClick={() => {
                  if (index > 0) {
                    const newFields = [...(props.value.Fields || [])];
                    [newFields[index - 1], newFields[index]] = [
                      newFields[index],
                      newFields[index - 1],
                    ];
                    props.onChange({ ...props.value, Fields: newFields });
                  }
                }}
                disabled={index === 0}
                margin="none"
                padding="none"
                tooltip="Flytta upp fält"
              />
              <EDIconButton
                icon={<RemoveCircleOutlineIcon />}
                onClick={() => {
                  const newFields = [...(props.value.Fields || [])];
                  newFields.splice(index, 1);
                  props.onChange({ ...props.value, Fields: newFields });
                }}
                margin="none"
                padding="none"
                tooltip="Ta bort fält"
              />
              <EDIconButton
                icon={<ArrowCircleDownIcon />}
                onClick={() => {
                  if (index < (props.value.Fields?.length || 0) - 1) {
                    const newFields = [...(props.value.Fields || [])];
                    [newFields[index], newFields[index + 1]] = [
                      newFields[index + 1],
                      newFields[index],
                    ];
                    props.onChange({ ...props.value, Fields: newFields });
                  }
                }}
                disabled={index === (props.value.Fields?.length || 0) - 1}
                margin="none"
                padding="none"
                tooltip="Flytta ner fält"
              />
            </Box>
          </Box>
        ))
      ) : (
        <EDTypography variant="body2" color="textSecondary">
          Tryck på plus-knappen för att lägga till fält.
        </EDTypography>
      )}
    </Box>
    <RapportKontrollEditPosition
      value={props.value}
      onChange={props.onChange}
    />
  </Box>
);
